'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import Container from '@src/components/Shared/Container';
import NextImage from '@src/components/Shared/NextImage';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import SectionHeader from '@src/components/Shared/SectionHeader';

import HeroValdProd from '@src/components/Sections/HeroValdProd';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { MoveHealthPageClientPropTypes } from './MoveHealthPage.types';
import HeroParallax from '@src/components/Shared/HeroParallax/HeroParallax';
import NextLink from '@src/components/Shared/NextLink';
import ScrollToWrapper from '@src/components/Shared/ScrollToWrapper';
import Testimonials from '@src/components/Sections/Testimonials';

const MoveHealthPageClient: React.FC<MoveHealthPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({
      languageCode: page?.languageCode?.toLowerCase(),
      slug: `/products/movehealth`,
      baseURL: '',
    })
  );
  setContentType('site-page');

  return (
    <>
      <div
        data-test='mh-page-hero-component'
        className='h-[52rem]'
        style={{
          backgroundImage: `url(${page?.hero?.items?.[0]?.mediaTertiary?.url})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
        data-id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
      >
        <HeroValdProd
          ctfData={page?.hero?.items?.[0]}
          id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
          className='my-[0rem]! h-full justify-center! md:flex-col! lg:flex-row! lg:justify-between!'
          imgClassName='absolute! lg:relative! opacity-[0.3] lg:opacity-[1] w-full lg:w-[60%] h-full overflow-visible! [&>img]:h-full! [&>img]:w-auto! [&>img]:object-cover! [&>img]:object-[-40rem] md:[&>img]:object-left [&>img]:overflow-visible [&>img]:max-w-none left-[-2.4rem] sm:left-[0] md:left-[-10rem] lg:left-[-33%]  2xl:left-[-12rem]'
          logoClassName='mb-[0]! mt-[0]! w-full lg:w-auto mx-auto lg:mx-[0rem] max-w-[40rem] md:max-w-[35rem]! lg:max-w-[40rem]! 2xl:max-w-[53.96rem]! xsm:min-h-[7rem] md:min-h-[6rem] lg:min-h-[12rem] 2xl:min-h-[14rem] [&>svg]:mb-[4rem] lg:[&>svg]:mb-[0rem] lg:min-h-[10rem]! xl:min-h-[12rem]!'
          introClassName='tracking-[-0.0342rem] leading-[2.7rem] [&>strong]:whitespace-nowrap text-[1.8rem] max-w-full md:text-[2rem] text-[#494949] md:leading-[3rem] md:tracking-[-0.036rem] lg:max-w-[48.5rem] xl:text-[2.1rem] lg:leading-[3.3rem] 2xl:max-w-[58.3rem]'
          headerClassName='order-1 xsm:mx-auto md:mx-[unset] md:text-left lg:self-center z-1 w-full md:w-[80%] bg-[#ffffffa1] lg:bg-[unset] p-[3rem] md:p-[4rem] lg:p-[0rem] rounded-2xl lg:rounded-none lg:w-[40%] md:mb-[3rem] md:mb-[0] lg:pl-[2rem]'
        >
          <RenderButtons
            className='justify-left'
            items={page?.hero?.items?.[0]?.buttonsCollection?.items}
          />
        </HeroValdProd>
      </div>

      <Container className='mt-[8rem] mb-[4rem]!'>
        <SectionHeader
          className='h-full lg:mb-[8rem]'
          pretitle={page?.digitalJourney?.items?.[0]?.preTitle}
          title={page?.digitalJourney?.items?.[0]?.title}
          titleClassName='text-left md:text-center [&>span]:drop-shadow-xs text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:text-[4.8rem] lg:leading-[5rem]'
          childrenClassName='relative'
          align='center'
        ></SectionHeader>
        <div className='mt-[4rem] grid gap-[2.4rem] gap-y-[7rem] md:grid-cols-2 lg:grid-cols-4'>
          {page?.digitalJourney?.items?.[0]?.topicCollection?.items.map((step: any) => {
            return (
              <ScrollToWrapper key={`${step?.sys?.id}-step`} href={step?.slug}>
                <div className='mx-auto flex max-w-[30rem] flex-col justify-between rounded-3xl bg-[#F9F9F9] p-[2.5rem] no-underline'>
                  <div className='text-center'>
                    <p className='text-primary mb-[2rem] text-[2.2rem] leading-[3rem] font-medium lg:text-[2.6rem]'>
                      {step?.title}
                    </p>
                    <RenderBodyCopy
                      className='mb-[3.2rem] leading-[2rem] font-extralight text-[#494949] [&>p]:text-[1.5rem]! lg:[&>p]:text-[1.7rem]!'
                      bodyCopy={step.bodyCopy?.json?.content}
                    />
                  </div>
                  <div className='relative top-[2rem] h-[0rem] justify-items-center md:top-[0rem]'>
                    <NextImage
                      className='absolute! top-[-2.7rem] w-full [&>img]:mx-auto! [&>img]:w-[8rem]! md:[&>img]:w-[12rem]!'
                      src={step?.mediaPrimary?.url}
                      alt={step?.mediaPrimary?.description}
                      objectFit='cover'
                    />
                  </div>
                </div>
              </ScrollToWrapper>
            );
          })}
        </div>
        <NextImage
          className='mx-auto mt-[8rem] md:max-w-[80%]'
          src={page?.imgDigitalJourney?.items?.[0]?.mediaPrimary?.url}
          alt={page?.imgDigitalJourney?.items?.[0]?.mediaPrimary?.description}
          objectFit='cover'
        />
      </Container>

      <Container className='mt-[4rem] mb-[4rem]!'>
        <SectionHeader
          className='mb-[4rem] h-full'
          pretitle={page?.simpleSteps?.items?.[0]?.preTitle}
          title={page?.simpleSteps?.items?.[0]?.title}
          titleClassName='text-left max-w-[65rem] md:text-center [&>span]:drop-shadow-xs text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:text-[4.8rem] lg:leading-[5rem]'
          childrenClassName='relative'
          align='center'
        ></SectionHeader>
        <RenderButtons
          className='mt-[0rem]! justify-center'
          items={page?.simpleSteps?.items?.[0]?.buttonCollection?.items}
        />
        <div className='mx-auto mt-[4rem] flex max-w-[140rem] flex-wrap justify-center gap-[2.4rem] gap-y-[2rem] md:gap-y-[5rem]'>
          {page?.simpleSteps?.items?.[0]?.topicCollection?.items.map(
            (step: any, index: number, array: any) => {
              const isLastItem = index === array.length - 1;
              return (
                <React.Fragment key={`${step?.sys?.id}-step`}>
                  <div className='flex flex-col justify-end md:p-[2.5rem]'>
                    <div className='relative mb-[3rem] justify-items-center'>
                      <NextImage
                        className='w-[22rem]'
                        src={step?.mediaPrimary?.url}
                        alt={step?.mediaPrimary?.description}
                        objectFit='cover'
                      />
                    </div>
                    <div className='text-center'>
                      <p className='mb-[2rem] text-[2.2rem] leading-[2.4rem] font-medium'>
                        {step?.title}
                      </p>
                      <RenderBodyCopy
                        className='text-body-copy 3xl:text-[1.8rem] mx-auto mb-[3.2rem] max-w-[26rem] text-[1.6rem]'
                        bodyCopy={step.bodyCopy?.json?.content}
                      />
                    </div>
                  </div>
                  {!isLastItem && (
                    <div className='hidden flex-col justify-end p-[2.5rem] xl:flex'>
                      <div className='relative mb-[3rem] h-[26rem] justify-items-center'>
                        <NextImage
                          className='h-auto w-[2rem]'
                          src='https://images.ctfassets.net/98s79sqwuajy/10vUL9oeyymdmqCQY0BGGd/bf3333525a00e51d33d8981cf8634a39/Frame.png'
                          alt='arrow between'
                          objectFit='contain'
                        />
                      </div>
                      <div className='text-center'></div>
                    </div>
                  )}
                </React.Fragment>
              );
            }
          )}
        </div>
      </Container>

      <Container
        id={page?.programBuilder?.items?.[0]?.scrollTo?.slice(1)}
        className={`mt-[8rem] flex flex-col gap-[3.2rem] overflow-y-hidden md:mt-[4rem] lg:flex-row lg:items-center`}
      >
        <div className='h-[23rem] lg:relative lg:z-20 lg:h-[50rem] lg:w-[60%]'>
          <iframe
            style={{ position: 'relative', top: 0, left: 0, width: '100%', height: '100%' }}
            src={`${page?.programBuilder?.items?.[0]?.extraAssetsCollection?.items?.[0]?.embedUrl}&background=1&loop=1&autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479`}
            allowFullScreen
            allow='autoplay; fullscreen; encrypted-media; picture-in-picture; loop;'
            title='Video player'
          />
        </div>

        <div className={`lg:relative lg:z-20 lg:w-[40%]`}>
          <SectionHeader
            className={`lg:relative lg:z-5 lg:py-[10rem]`}
            pretitle={page?.programBuilder?.items?.[0]?.preTitle}
            title={page?.programBuilder?.items?.[0]?.title}
            preTitleClassName='mb-[1.6rem]'
            titleClassName={`text-[2.8rem] leading-[3.2rem] mb-[1.8rem] lg:mb-[3.2rem] lg:text-[4.8rem] lg:leading-[5rem] lg:tracking-[-0.096rem]`}
            childrenClassName='relative'
            align='left'
          >
            <RenderBodyCopy bodyCopy={page?.programBuilder?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons
              className='justify-left mt-[4rem]'
              items={page?.programBuilder?.items?.[0]?.buttonCollection?.items}
            />
          </SectionHeader>
        </div>
      </Container>

      <HeroParallax data={page?.exercises?.items?.[0]} />

      <ImageTextSecondary
        reversed
        id={page?.questionaires?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='mb-[4rem]! overflow-visible! py-[3.2rem] md:mb-[8rem]! lg:py-[6.4rem] 2xl:py-[8rem]'
        pretitle={page?.questionaires?.items?.[0]?.preTitle}
        title={page?.questionaires?.items?.[0]?.title}
        titleClassName='lg:max-w-[60rem] [&>span]:drop-shadow-xs [&>span]:whitespace-nowrap!'
        imgURL={page?.questionaires?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        imgFirstClassName='mt-[-1rem] md:mt-[-2rem] lg:mt-[unset]'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.questionaires?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons items={page?.questionaires?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextSecondary
        id={page?.education?.items?.[0]?.scrollTo?.slice(1)}
        className='mb-[2rem]! overflow-visible! py-[3.2rem] md:mb-[8rem]! lg:py-[6.4rem] 2xl:py-[8rem]'
        pretitle={page?.education?.items?.[0]?.preTitle}
        title={page?.education?.items?.[0]?.title}
        titleClassName='lg:max-w-[60rem] [&>span]:drop-shadow-xs'
        imgURL={page?.education?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        imgFirstClassName='mt-[-1rem] md:mt-[-2rem] lg:mt-[unset]'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.education?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons items={page?.education?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <NextImage
        src={page?.results?.items?.[0]?.mediaSecondary?.url}
        alt='A portrait'
        className='mb-[4.5rem] h-[65rem] overflow-hidden overflow-y-hidden md:h-[75rem] lg:h-[78rem] 2xl:mb-[5.6rem] [&>img]:object-top [&>img]:opacity-[0.6]'
      >
        <Container
          id={page?.results?.items?.[0]?.scrollTo?.slice(1)}
          className='mb-[0rem]! pt-[4rem] md:pt-[8rem]'
        >
          <div className='flex flex-col items-center'>
            <SectionHeader
              className='h-full'
              pretitle={page?.results?.items?.[0]?.preTitle}
              title={page?.results?.items?.[0]?.title}
              preTitleClassName='mb-[1.6rem]'
              titleClassName='text-left md:text-center text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[90rem] xl:max-w-[100rem] lg:text-[4.8rem] lg:leading-[5rem]'
              childrenClassName='relative'
              align='center'
            >
              <RenderBodyCopy
                className='mx-auto mt-[2.4rem] max-w-[51.7rem] text-left md:text-center xl:max-w-full! 2xl:mt-[3.2rem]'
                bodyCopy={page?.results?.items?.[0]?.bodyCopy?.json?.content}
              />
              <RenderButtons
                className='justify-center'
                items={page?.results?.items?.[0]?.buttonCollection?.items}
              />
            </SectionHeader>
            <div className='relative top-[-6rem] h-[44rem] w-5xl scale-[0.56] sm:top-[3rem] sm:scale-[0.8] md:top-[4rem] md:w-full md:scale-[1]'>
              <div className='mx-auto h-[45rem] w-5xl overflow-hidden rounded-[3rem]'>
                <iframe
                  style={{
                    position: 'relative',
                    top: '-27px',
                    left: '-33px',
                    width: '720px',
                    height: '500px',
                  }}
                  src={`${page?.results?.items?.[0]?.extraAssetsCollection?.items?.[0]?.embedUrl}&background=1&loop=1&autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479`}
                  allowFullScreen
                  allow='autoplay; fullscreen; encrypted-media; picture-in-picture; loop;'
                  title='Video player'
                />
              </div>
            </div>
          </div>
        </Container>
      </NextImage>

      <ImageTextSecondary
        reversed
        className='3xl:py-[8rem] mb-[0rem]! overflow-visible! py-[4rem]'
        titleClassName='[&>span]:drop-shadow-xs'
        id={page?.topics?.items?.[0]?.scrollTo?.slice(1)}
        imgURL={page?.topics?.items?.[0]?.mediaPrimary?.url}
        title={page?.topics?.items?.[0]?.title}
        pretitle={page?.topics?.items?.[0]?.preTitle}
        childrenElements={
          <>
            <RenderBodyCopy
              className='[&>p>a]:drop-shadow-xs'
              bodyCopy={page?.topics?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons items={page?.topics?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container
        id={page?.integratedNorms?.items?.[0]?.scrollTo?.slice(1)}
        className='mb-[0rem]! pt-[4rem] md:pt-[8rem]'
        background=''
        bgStyle={{
          backgroundImage: `url(${page?.integratedNorms?.items?.[0]?.mediaPrimary?.url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <SectionHeader
          className='h-full'
          pretitle={page?.integratedNorms?.items?.[0]?.preTitle}
          title={page?.integratedNorms?.items?.[0]?.title}
          preTitleClassName='mb-[1.6rem]'
          titleClassName='text-left md:text-center xl:locale-fr:max-w-[94rem] xl:locale-es:max-w-[79rem] text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[70rem] lg:text-[4.8rem] lg:leading-[5rem]'
          childrenClassName='relative'
          align='center'
        >
          <RenderBodyCopy
            className='mx-auto mt-[2.4rem] max-w-[51.7rem] text-left md:text-center xl:max-w-full! 2xl:mt-[3.2rem]'
            bodyCopy={page?.integratedNorms?.items?.[0]?.bodyCopy?.json?.content}
          />
          <RenderButtons
            className='justify-center'
            items={page?.integratedNorms?.items?.[0]?.buttonCollection?.items}
          />
        </SectionHeader>

        <div className='mx-auto max-w-[55rem] overflow-hidden rounded-[1.6rem] md:max-w-[100rem]'>
          <NextImage
            className='h-auto w-full md:mb-[-6rem]!'
            src={page?.integratedNorms?.items?.[0]?.mediaSecondary?.url}
            alt=''
            aspectRatio={
              page?.integratedNorms?.items?.[0]?.mediaSecondary?.width /
              page?.integratedNorms?.items?.[0]?.mediaSecondary?.height
            }
            width={page?.integratedNorms?.items?.[0]?.mediaSecondary?.width}
          />
        </div>
      </Container>

      <Container
        id={page?.patientApp?.items?.[0]?.scrollTo?.slice(1)}
        className={`mt-[0rem] flex flex-col gap-[3.2rem] overflow-y-hidden lg:mt-[8rem] lg:flex-row lg:items-center`}
      >
        <div className='h-[20rem] md:h-[30rem] lg:relative lg:z-20 lg:h-[50rem] lg:w-[60%] lg:scale-[1.15]'>
          <iframe
            style={{
              position: 'relative',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
            src={`${page?.patientApp?.items?.[0]?.extraAssetsCollection?.items?.[0]?.embedUrl}&background=1&loop=1&autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479`}
            allowFullScreen
            allow='autoplay; fullscreen; encrypted-media; picture-in-picture; loop;'
            title='Video player'
          />
        </div>

        <div className={`lg:relative lg:z-20 lg:w-[40%]`}>
          <SectionHeader
            className={`lg:relative lg:z-5 lg:py-[10rem]`}
            pretitle={page?.patientApp?.items?.[0]?.preTitle}
            title={page?.patientApp?.items?.[0]?.title}
            preTitleClassName='mb-[1.6rem]'
            titleClassName={`text-[2.8rem] leading-[3.2rem] mb-[1.8rem] lg:mb-[3.2rem] lg:text-[4.8rem] lg:leading-[5rem] lg:tracking-[-0.096rem]`}
            childrenClassName='relative'
            align='left'
          >
            <RenderBodyCopy bodyCopy={page?.patientApp?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons
              className='justify-left mt-[4rem]'
              items={page?.patientApp?.items?.[0]?.buttonCollection?.items}
            />
          </SectionHeader>
        </div>
      </Container>

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
        background='bg-background-secondary'
      />

      <ImageTextSecondary
        reversed
        className='locale-en-ca:hidden! overflow-visible! py-[3.2rem] lg:py-[6.4rem] 2xl:py-[8rem]'
        id={page?.easyAssign?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        imgURL={page?.easyAssign?.items?.[0]?.mediaPrimary?.url}
        imgFirstClassName='order-2 md:order-1'
        imgSecondClassName='lg:[&_figure]:w-[80%]'
        sectionHeaderClassName='order-1 mt-[5rem] md:order-2'
        title={page?.easyAssign?.items?.[0]?.title}
        pretitle={page?.easyAssign?.items?.[0]?.preTitle}
        childrenElements={
          <>
            <RenderBodyCopy
              className='[&>p>a]:drop-shadow-xs'
              bodyCopy={page?.easyAssign?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons items={page?.easyAssign?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container className='mt-[8rem]'>
        <SectionHeader
          className='h-full lg:mb-[4rem]'
          pretitle={page?.nowMoveHealth?.items?.[0]?.preTitle}
          title={page?.nowMoveHealth?.items?.[0]?.title}
          titleClassName='text-left max-w-[85rem] md:text-center [&>span]:drop-shadow-xs text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:text-[4.8rem] lg:leading-[5rem]'
          childrenClassName='relative'
          align='center'
        ></SectionHeader>
        <RenderButtons
          className='mt-[0rem]! justify-center'
          items={page?.nowMoveHealth?.items?.[0]?.buttonCollection?.items}
        />
        <div className='mx-auto mt-[4rem] flex max-w-[140rem] flex-col justify-center gap-[2.4rem] md:flex-row md:gap-y-[5rem]'>
          {page?.nowMoveHealth?.items?.[0]?.topicCollection?.items.map((step: any) => {
            return (
              <div
                key={`${step?.sys?.id}-step`}
                className='flex  flex-col justify-end md:p-[2.5rem] md:w-[50%]'
              >
                <NextLink
                  href={step?.slug}
                  target='_blank'
                  className='relative mb-[3rem] justify-items-center'
                >
                  <NextImage
                    className='w-[100%] rounded-2xl shadow-[0_4px_20px_0_rgba(0,0,0,0.25)]'
                    src={step?.mediaPrimary?.url}
                    alt={step?.mediaPrimary?.description}
                    objectFit='cover'
                  />
                </NextLink>
              </div>
            );
          })}
        </div>
      </Container>
    </>
  );
};

export default MoveHealthPageClient;
